import React, { useContext, useState } from 'react'

import { NewShoppingCartItem } from '../../../hooks/useShoppingCart'
import { LocaleContext, TranslationKey } from '../../../locale/i18n'
import { LinkParameterSlug, Product } from '../../../logic/catalog'
import Button from '../../Button'
import Dropdown from '../../Dropdown'
import IconCart from '../../icons/IconCart'
import IconTooltip from '../../icons/IconTooltip'
import Tooltip from '../../Tooltip'

import PricingBreakdown from './PricingBreakdown'
import { AskForOfferButton } from './ProductCTABanner'

interface Props {
  className?: string
  product: Product
  onSubmit(item: NewShoppingCartItem): void
}

const ProductConfiguration = ({ className, product, onSubmit }: Props) => {
  const { t } = useContext(LocaleContext)

  const allParams = product.parameters ? [...product.parameters] : []
  if (product.tatParameter) {
    allParams.push(product.tatParameter)
  }
  const [parameters, setParameters] = useState<
    Record<string, string | undefined>
  >(() => {
    const params = Object.fromEntries(
      product.parameters?.map(param => [param.id, param.options[0]?.id]) ?? [],
    )
    if (product.tatParameter) {
      params[product.tatParameter.id] = product.tatParameter.options[0]?.id
    }
    return params
  })

  return (
    <form
      className={className}
      onSubmit={e => {
        e.preventDefault()
        onSubmit(
          NewShoppingCartItem.parse({
            parameters,
            productId: product.id,
          }),
        )
      }}
    >
      <h3 className="text-center font-primary text-lg font-semibold">
        {t('productPage.productConfiguration.heading')}
      </h3>
      {allParams.map(({ helperText, id, options, title, slug }) => (
        <label
          className="flex flex-col gap-2"
          data-test-id={`parameter-${slug}`}
          htmlFor={`input-${id}`}
          key={id}
        >
          <div className="flex items-center gap-2 font-medium">
            {title}
            {helperText && (
              <Tooltip label={helperText}>
                <IconTooltip className="block cursor-help" />
              </Tooltip>
            )}
            {
              // If parameter slug is listed to require additional link, add the link localizations
              Object.values<string>(LinkParameterSlug).includes(slug) && (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={t(
                    `productPage.productConfiguration.slugLink.${slug}.url` as TranslationKey,
                  )}
                >
                  {t(
                    `productPage.productConfiguration.slugLink.${slug}.label` as TranslationKey,
                  )}
                </a>
              )
            }
          </div>
          <Dropdown
            className="w-fit"
            selectId={`input-${id}`}
            onChange={v => {
              if (v) {
                setParameters({ ...parameters, [id]: v })
              }
            }}
            options={options.map(o => ({
              label: o.displayName ?? '',
              value: o.id,
            }))}
            value={parameters[id]}
          />
        </label>
      ))}
      <PricingBreakdown product={product} parameters={parameters} />
      <div className="flex flex-col gap-3">
        <AskForOfferButton size="xl" />
        <Button
          size="xl"
          variant="outline"
          data-gtm-id="productPageGoal"
          data-test-id="add-to-cart"
          type="submit"
        >
          <IconCart />
          {t('productPage.productConfiguration.addToCart')}
        </Button>

        <p className="text-center text-xs text-gray-500">
          {t('productPage.productConfiguration.samplesNote')}
        </p>
        <p className="text-center text-xs text-gray-500">
          {t('productPage.productConfiguration.expertReview')}
        </p>
      </div>
    </form>
  )
}

export default ProductConfiguration
